import React, { useState, useEffect } from "react";
import { X, Smartphone, Lock, ShieldCheck } from "lucide-react";
import { sendOtpUnlockCourse, verifyOtpUnlockCourse, createOrder, verifyPayment } from "../../api/api";
import { getToken, storeAuthData } from "../../api/storageService";

const PaymentPopup = ({ 
    open, 
    onClose, 
    program, 
    variant, 
    formatPrice, 
    getPriceWithGST,
    onPaymentSuccess 
}) => {
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [otpSent, setOtpSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [isVerified, setIsVerified] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: ""
    });

    // Program details
    const currentVariant = variant;
    const price = currentVariant ? parseFloat(currentVariant.price) : 9999;
    const gstPercentage = currentVariant?.gst_percentage || 18;
    const totalPrice = getPriceWithGST ? getPriceWithGST(price, gstPercentage) : price + (price * gstPercentage / 100);

    // Reset form when popup opens - MOVED TO TOP LEVEL
    useEffect(() => {
        if (open) {
            setFormData({
                firstName: "",
                lastName: "",
                email: ""
            });
            setPhoneNumber("");
            setOtp(["", "", "", ""]);
            setOtpSent(false);
            setIsVerified(false);
            setError("");
            setSuccess("");
        }
    }, [open]);

    // Don't render if not open - EARLY RETURN AFTER ALL HOOKS
    if (!open) return null;

    const startResendTimer = (duration) => {
        setResendTimer(duration);
        const timer = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < 3) {
                document.getElementById(`payment-otp-${index + 1}`)?.focus();
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            document.getElementById(`payment-otp-${index - 1}`)?.focus();
        }
    };

    const handleSendOTP = async () => {
        if (phoneNumber.length < 10) {
            setError("Please enter a valid 10-digit phone number");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await sendOtpUnlockCourse(phoneNumber, program?.name || "");

            if (response.status) {
                setOtpSent(true);
                setSuccess("OTP sent successfully!");
                startResendTimer(60);
                setError("");
                setOtp(["", "", "", ""]);
                setTimeout(() => document.getElementById('payment-otp-0')?.focus(), 100);
            } else {
                setError(response.message || "Failed to send OTP");
            }
        } catch (error) {
            console.error("Send OTP error:", error);
            setError(error?.response?.data?.message || "Failed to send OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        const otpString = otp.join("");
        if (otpString.length < 4) {
            setError("Please enter a valid 4-digit OTP");
            return;
        }

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            const response = await verifyOtpUnlockCourse(phoneNumber, otpString);
            if (response.status) {
                const stored = storeAuthData(response);
                const token = getToken();
                setIsVerified(true);
                setSuccess("Phone number verified successfully!");
                setError("");
                
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } else {
                setError(response.message || "Invalid OTP. Please try again.");
            }
        } catch (error) {
            console.error("Verify OTP error:", error);
            setError(error?.response?.data?.message || "Failed to verify OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOTP = () => {
        if (resendTimer === 0) {
            handleSendOTP();
        }
    };
    const handlePayment = async () => {
        if (!formData.firstName.trim()) {
            setError("Please enter your first name");
            return;
        }
        if (!formData.lastName.trim()) {
            setError("Please enter your last name");
            return;
        }
        if (!formData.email.trim() || !formData.email.includes('@')) {
            setError("Please enter a valid email address");
            return;
        }
        if (!isVerified) {
            setError("Please verify your phone number first");
            return;
        }

        const token = getToken();        
        if (!token) {
            setError("Authentication failed. Please verify OTP again.");
            return;
        }

        setIsProcessingPayment(true);
        setError("");

        try {
            const orderData = {
                program_id: program?.id,
                variant_id: variant?.id,
                payment_method: "razorpay",
                auto_renew: true,
                name: formData.firstName + " " + formData.lastName,
                email: formData.email
            };
            
            const orderResponse = await createOrder(orderData);
            
            if (orderResponse?.status === true && orderResponse?.data) {
                const apiData = orderResponse.data;
                const razorpayOrderId = apiData.order_id;
                const razorpayKeyId = apiData.razorpay_key;
                const amountFromApi = apiData.amount;
                const purchaseId = apiData.purchase_id;
              
                if (razorpayOrderId && razorpayKeyId) {
                    initializeRazorpayPayment(
                        razorpayOrderId,
                        razorpayKeyId,
                        amountFromApi,
                        program,
                        variant,
                        purchaseId
                    );
                } else {
                    console.error('Missing Razorpay order details:', apiData);
                    setError('Failed to create payment order. Please try again.');
                    setIsProcessingPayment(false);
                }
            } else {
                console.error('API returned unsuccessful response:', orderResponse);
                setError(orderResponse?.message || 'Failed to create order. Please try again.');
                setIsProcessingPayment(false);
            }

        } catch (error) {
            console.error("Payment Error:", error);
            
            if (error.response) {
                console.error('Error response:', error.response.data);
                if (error.response.data?.message) {
                    setError(error.response.data.message);
                } else if (error.response.status === 401) {
                    setError('Session expired. Please login again.');
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 1500);
                } else {
                    setError('Failed to create order. Please try again.');
                }
            } else {
                setError(error?.message || "Payment failed. Please try again.");
            }
            setIsProcessingPayment(false);
        }
    };

    const initializeRazorpayPayment = (razorpayOrderId, razorpayKeyId, amountInPaise, programData, variantData, purchaseId) => {
        if (window.Razorpay) {
            openRazorpay(razorpayOrderId, razorpayKeyId, amountInPaise, programData, variantData, purchaseId);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
            openRazorpay(razorpayOrderId, razorpayKeyId, amountInPaise, programData, variantData, purchaseId);
        };
        script.onerror = () => {
            setError('Failed to load payment gateway. Please refresh the page.');
            setIsProcessingPayment(false);
        };
        document.body.appendChild(script);
    };

    const openRazorpay = (razorpayOrderId, razorpayKeyId, amountInPaise, programData, variantData, purchaseId) => {
        const options = {
            key: razorpayKeyId,
            amount: amountInPaise,
            currency: 'INR',
            name: 'Nutrillion Academy',
            description: `${programData?.name || 'Program'} - ${variantData?.duration || ''}`,
            order_id: razorpayOrderId,
            prefill: {
                name: formData.firstName + " " + formData.lastName,
                email: formData.email,
                contact: phoneNumber
            },
            theme: {
                color: '#16a34a'
            },
            modal: {
                ondismiss: () => {
                    setError('Payment cancelled');
                    setIsProcessingPayment(false);
                }
            },
            handler: async (response) => {
                await verifyProgramPayment(response, razorpayOrderId, purchaseId);
            }
        };

        try {
            const razorpay = new window.Razorpay(options);
            
            razorpay.on('payment.failed', (response) => {
                console.error('Payment failed:', response);
                setError(`Payment failed: ${response.error?.description || 'Please try again'}`);
                setIsProcessingPayment(false);
            });
            
            razorpay.open();
        } catch (error) {
            console.error('Razorpay initialization error:', error);
            setError('Failed to initialize payment. Please try again.');
            setIsProcessingPayment(false);
        }
    };

    const verifyProgramPayment = async (paymentResponse, razorpayOrderId, purchaseId) => {
        setSuccess('Verifying payment...');
        
        try {
            const verificationData = {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                purchase_id: purchaseId
            };
                  
            const verificationResult = await verifyPayment(verificationData);
            
            if (verificationResult?.status === true || verificationResult?.success === true) {
                setSuccess('Payment successful! Your purchase is confirmed. 🎉');
                if (onPaymentSuccess) {
                    onPaymentSuccess();
                }
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setError(verificationResult?.message || 'Payment verification failed. Please contact support.');
            }
            
        } catch (error) {
            console.error('Payment verification error:', error);
            setError(error?.response?.data?.message || 'Payment verification failed. Please contact support.');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    return (
        <div className="fixed inset-0 font-lato bg-black/40 flex items-center justify-center z-50 px-4">
            <div className="w-full md:max-w-[650px] max-w-[95%] bg-white rounded-lg shadow-xl relative max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <button
                    onClick={onClose}
                    className="absolute right-3 top-3 bg-white rounded-full w-6 h-6 text-gray-700 z-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                    <X size={16} />
                </button>

                <div className="p-5">
                    {error && (
                        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="mb-3 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md text-sm">
                            {success}
                        </div>
                    )}

                    {/* Phone Verification */}
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex gap-3 items-center mb-3">
                            <div className="bg-green-600 text-white p-2 rounded-full">
                                <Smartphone size={16} />
                            </div>
                            <div>
                                <h4 className="text-[15px] font-bold text-green-700">
                                    Mobile Verification
                                </h4>
                                <p className="text-[12px] text-green-600">
                                    Verify your phone to proceed with payment
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <div className="border rounded bg-white px-3 py-2 text-sm flex items-center">
                                +91
                            </div>
                            <input
                                placeholder="Enter mobile number"
                                className="flex-1 border rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-500"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                                maxLength={10}
                                disabled={otpSent}
                            />
                            <button
                                onClick={handleSendOTP}
                                disabled={isLoading || otpSent || phoneNumber.length < 10}
                                className="bg-green-600 text-white rounded px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap hover:bg-green-700 transition"
                            >
                                {isLoading ? "Sending..." : otpSent ? "OTP Sent ✓" : "Send OTP"}
                            </button>
                        </div>

                        {otpSent && (
                            <>
                                <p className="text-xs text-green-700 mt-3">
                                    Enter 4-digit OTP sent to +91 {phoneNumber}
                                </p>
                                <div className="flex items-center gap-3 mt-2">
                                    <div className="flex gap-2">
                                        {otp.map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`payment-otp-${index}`}
                                                type="text"
                                                maxLength="1"
                                                className="w-12 h-12 text-center text-lg font-bold bg-white border rounded-md focus:ring-2 focus:ring-green-500 outline-none"
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                disabled={isVerified}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        onClick={handleVerifyOTP}
                                        disabled={isLoading || isVerified}
                                        className="bg-green-600 text-white rounded px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition"
                                    >
                                        {isLoading ? "Verifying..." : isVerified ? "✓ Verified" : "Verify"}
                                    </button>
                                </div>
                                <div className="flex justify-between items-center mt-3">
                                    {isVerified ? (
                                        <span className="bg-green-100 text-green-700 rounded-full px-3 py-1 text-xs font-medium">
                                            ✓ Number Verified
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-500">
                                            Didn't get it?
                                            {resendTimer > 0 ? (
                                                ` Resend in ${resendTimer}s`
                                            ) : (
                                                <button
                                                    onClick={handleResendOTP}
                                                    className="text-green-600 underline ml-1 font-medium"
                                                >
                                                    Resend OTP
                                                </button>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* User Details Form - NO AUTO-FILL */}
                    <div className={`mt-4 space-y-3 transition-all duration-300 ${isVerified ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-gray-600 font-medium">First Name *</label>
                                <input
                                    placeholder="Enter your first name"
                                    className="w-full border rounded px-3 py-2 text-sm mt-1 outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    disabled={!isVerified}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 font-medium">Last Name *</label>
                                <input
                                    placeholder="Enter your last name"
                                    className="w-full border rounded px-3 py-2 text-sm mt-1 outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    disabled={!isVerified}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs text-gray-600 font-medium">Email Address *</label>
                            <input
                                placeholder="Enter your email address"
                                className="w-full border rounded px-3 py-2 text-sm mt-1 outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                disabled={!isVerified}
                                type="email"
                            />
                        </div>
                    </div>

                    {/* Payment Button */}
                    <button
                        onClick={handlePayment}
                        disabled={!isVerified || isLoading || isProcessingPayment}
                        className="mt-5 w-full bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-md py-3.5 text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isProcessingPayment ? (
                            <>
                                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                                Processing Payment...
                            </>
                        ) : (
                            <>
                                <Lock size={16} />
                                Pay ₹{totalPrice.toFixed(0)} Securely
                            </>
                        )}
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
                        <ShieldCheck size={13} className="text-green-500" />
                        Secured with 256-bit encryption
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PaymentPopup;